import { useMemo } from 'react';
import { InfoSection } from './components/InfoSection';
import { Button } from '@tempmee/react';
import { Row } from '../../../commonComponents';
import { formatBenefits, formatPayRange, formatPayTitle } from './utils/formatters';
import { EnumHelper } from '../../../utils/EnumHelper';
import { UserProfession, UserSubProfession } from '../../../enums/UserProfession';
import { IEditableFormProps } from './Types';
import { GeneralJobInfoForm, GeneralJobInfoPreview } from './components/GeneralJobInfo';
import { EducationUtils } from '~/utils/EducationUtils';

export const GeneralJobInfoEditable = ({
  jobPosting,
  editMode,
  hideEditButton,
  disableSave,
  control,
  errors,
  setValue,
  onEditClick,
  onCancelClick,
  onSaveClick,
  readonlyFields,
}: IEditableFormProps) => {
  const {
    benefits,
    benefitsOther,
    maxRate,
    minRate,
    officeName,
    officeSpecialty,
    payType,
    professionType,
    subProfession,
    title,
  } = jobPosting || {};

  // Format display values
  const payTitleDisplay = formatPayTitle(minRate, maxRate);
  const payRangeDisplay = formatPayRange(minRate, maxRate, payType);
  const benefitsDisplay = formatBenefits(benefits?.filter((it) => it !== 'Other') || []);

  // Get profession display name (subProfession takes precedence)
  const professionDisplay = useMemo(() => {
    const profToDisplay = subProfession || professionType;
    if (!profToDisplay) return '';

    const isSubProfession = EnumHelper.getObjectKeys(UserSubProfession).includes(profToDisplay);
    return isSubProfession
      ? UserSubProfession.getName(profToDisplay)
      : UserProfession.getName(profToDisplay);
  }, [professionType, subProfession]);

  if (editMode) {
    return (
      <InfoSection
        icon="briefcase-solid"
        title="General Job Information"
        dataCy="general-info-section"
        rightAction={
          <Row gap={10}>
            <Button
              data-cy="edit-general-info-cancel"
              variant="secondary"
              size="xs"
              onClick={onCancelClick}
            >
              Cancel
            </Button>
            <Button
              data-cy="edit-general-info-save"
              disabled={disableSave}
              size="xs"
              onClick={onSaveClick}
            >
              Save
            </Button>
          </Row>
        }
      >
        <GeneralJobInfoForm
          jobPosting={jobPosting}
          control={control}
          errors={errors}
          setValue={setValue}
          readonlyFields={readonlyFields}
        />
      </InfoSection>
    );
  }

  return (
    <InfoSection
      icon="briefcase-solid"
      title="General Job Information"
      dataCy="general-info-section"
      rightActionIcon="edit-pencil"
      onRightActionPress={hideEditButton ? undefined : onEditClick}
    >
      <GeneralJobInfoPreview
        officeName={officeName || ''}
        title={title || ''}
        professionType={professionDisplay || ''}
        specialty={EducationUtils.removeSpecialtyPrefix(officeSpecialty || '')}
        payType={payType || ''}
        payTitle={payTitleDisplay}
        payRange={payRangeDisplay}
        benefits={benefitsDisplay}
        benefitsOther={benefitsOther || ''}
      />
    </InfoSection>
  );
};
